import { useCallback, useEffect, useRef, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import {
  Plus,
  Edit2,
  Eye,
  Download,
  Search,
  Loader2,
  X,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Trash2,
  PackageMinus,
  PackageCheck,
  Ban,
  Receipt,
  AlertTriangle,
  FileText,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import BaseApi from "../../services/base-api";
import { PurchaseReturnsApi } from "../../services/purchase-returns-api";
import Price from "../../components/Currency/Price";
import ConfirmModal from "../../components/Modals/Confirm";
import LoadingComponent from "../../components/LoadingComponent";
import Pagination from "../../components/Pagination";
import { useDebounce } from "../../hooks/useDebounce";
import PurchaseReturnFormModal from "../../components/PurchaseReturns/PurchaseReturnFormModal";
import {
  formatDate,
  getItemDisplay,
  getPersonName,
  PurchaseReturnStatusBadge,
  PURCHASE_RETURN_STATUS_CONFIG,
  RefundStatusBadge,
  toNumber,
} from "../../components/PurchaseReturns/helpers";
import type {
  PurchaseReturn,
  PurchaseReturnRefundStatus,
  PurchaseReturnStats,
  PurchaseReturnUser,
} from "../../types/PurchaseReturn";

type ActionType = "approve" | "reject" | "complete" | "cancel" | "refund";

interface ActionState {
  type: ActionType;
  target: PurchaseReturn;
}

const PurchaseReturnManagementPage = () => {
  const { success, err } = useToast();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { permissions } = useAuth();
  const canCreate = hasPermission(
    permissions,
    PERMISSIONS.CREATE_PURCHASE_RETURNS,
  );
  const canUpdate = hasPermission(
    permissions,
    PERMISSIONS.EDIT_PURCHASE_RETURNS,
  );
  const canApprove = hasPermission(
    permissions,
    PERMISSIONS.APPROVE_PURCHASE_RETURNS,
  );
  const canComplete = hasPermission(
    permissions,
    PERMISSIONS.COMPLETE_PURCHASE_RETURNS,
  );
  const canCancel = hasPermission(
    permissions,
    PERMISSIONS.CANCEL_PURCHASE_RETURNS,
  );
  const canDelete = hasPermission(
    permissions,
    PERMISSIONS.DELETE_PURCHASE_RETURNS,
  );

  const [purchaseReturns, setPurchaseReturns] = useState<PurchaseReturn[]>([]);
  const [stats, setStats] = useState<PurchaseReturnStats | null>(null);
  const [suppliers, setSuppliers] = useState<PurchaseReturnUser[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const latestRequestRef = useRef(0);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim());
  const [statusFilter, setStatusFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [apiTotalItems, setApiTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [formModal, setFormModal] = useState<{
    open: boolean;
    editing: PurchaseReturn | null;
    purchaseOrderId?: string;
  }>({ open: false, editing: null });
  const [detailReturn, setDetailReturn] = useState<PurchaseReturn | null>(null);
  const [action, setAction] = useState<ActionState | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: t("Confirm"),
    cancelText: t("Cancel"),
    onConfirm: () => {},
  });

  const fetchPurchaseReturns = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const res = await PurchaseReturnsApi.list(currentPage, itemsPerPage, {
        status: statusFilter,
        supplierId: supplierFilter,
        search: debouncedSearch,
        startDate,
        endDate,
      });
      if (requestId !== latestRequestRef.current) return;
      if (res?.success) {
        setPurchaseReturns(res.data || []);
        setApiTotalItems(res.pagination?.totalItems ?? (res.data || []).length);
      } else {
        err(res?.message || t("Failed to load purchase returns"));
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Error fetching purchase returns:", error);
      err(t("Failed to load purchase returns"));
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  }, [
    currentPage,
    itemsPerPage,
    statusFilter,
    supplierFilter,
    debouncedSearch,
    startDate,
    endDate,
    err,
    t,
  ]);

  const fetchStats = async () => {
    try {
      const res = await PurchaseReturnsApi.getStats();
      if (res?.success && res.data) {
        setStats(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch purchase return stats:", error);
    }
  };

  // Same supplier source as the purchase order page
  const fetchSuppliers = async () => {
    try {
      const res = await BaseApi.get("/users?role=supplier");
      if (res?.success && res.data) {
        setSuppliers(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchSuppliers();
  }, []);

  // Deep link from the purchase order view: ?purchaseOrderId=<id>
  useEffect(() => {
    const purchaseOrderId = searchParams.get("purchaseOrderId");
    if (!purchaseOrderId) return;

    if (canCreate) {
      setFormModal({ open: true, editing: null, purchaseOrderId });
    }
    searchParams.delete("purchaseOrderId");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPurchaseReturns();
  }, [fetchPurchaseReturns]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, supplierFilter, startDate, endDate]);

  const refreshAll = () => {
    fetchPurchaseReturns();
    fetchStats();
  };

  const refreshDetail = async (id: string, fallback?: PurchaseReturn) => {
    const res = await PurchaseReturnsApi.getById(id);
    if (res?.success && res.data) {
      setDetailReturn(res.data);
    } else if (fallback) {
      setDetailReturn(fallback);
    }
  };

  const handleView = async (pr: PurchaseReturn) => {
    const res = await PurchaseReturnsApi.getById(pr.id);
    if (res?.success && res.data) {
      setDetailReturn(res.data);
    } else {
      err(res?.message || t("Failed to load purchase return"));
    }
  };

  const handleEdit = async (pr: PurchaseReturn) => {
    // Items are needed to pre-fill the form; list rows may not carry them
    const res = await PurchaseReturnsApi.getById(pr.id);
    if (res?.success && res.data) {
      setDetailReturn(null);
      setFormModal({ open: true, editing: res.data });
    } else {
      err(res?.message || t("Failed to load purchase return"));
    }
  };

  const openNewReturnModal = () => {
    setFormModal({ open: true, editing: null });
  };

  const closeFormModal = () => {
    setFormModal({ open: false, editing: null });
  };

  const handleFormSaved = (saved?: PurchaseReturn) => {
    closeFormModal();
    refreshAll();
    if (saved?.id) {
      refreshDetail(saved.id, saved);
    }
  };

  const openConfirmModal = (config: {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
  }) => {
    setConfirmConfig({
      isOpen: true,
      title: config.title ?? t("Confirm Action"),
      message: config.message ?? t("Are you sure?"),
      confirmText: config.confirmText ?? "Confirm",
      cancelText: config.cancelText ?? t("Cancel"),
      onConfirm: () => {
        config.onConfirm();
        closeConfirmModal();
      },
    });
  };

  const closeConfirmModal = () => {
    setConfirmConfig((c) => ({ ...c, isOpen: false }));
  };

  // DELETE /purchase-returns/{id} - soft delete, pending only
  const handleDelete = (pr: PurchaseReturn) => {
    openConfirmModal({
      title: t("Delete Purchase Return"),
      message: t("Are you sure you want to delete this purchase return?"),
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          const res = await PurchaseReturnsApi.remove(pr.id);
          if (res?.success) {
            success(t("Purchase return deleted!"));
            if (detailReturn?.id === pr.id) setDetailReturn(null);
            refreshAll();
          } else {
            err(res?.message || t("Failed to delete purchase return"));
          }
        } catch (error) {
          console.error("Failed to delete purchase return:", error);
          err(t("Failed to delete purchase return"));
        }
      },
    });
  };

  const getDownloadFileName = (
    pr: PurchaseReturn,
    contentDisposition: string | null,
  ) => {
    const matchedFileName = contentDisposition?.match(
      /filename\*?=(?:UTF-8''|")?([^";]+)/i,
    )?.[1];

    if (matchedFileName) {
      return decodeURIComponent(matchedFileName.replace(/"/g, ""));
    }

    return `purchase-return-${pr.returnNumber || pr.id}.pdf`;
  };

  const handleDownload = async (pr: PurchaseReturn) => {
    if (downloadingId) return;

    setDownloadingId(pr.id);
    try {
      const { blob, contentDisposition } = await PurchaseReturnsApi.downloadPdf(
        pr.id,
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        getDownloadFileName(pr, contentDisposition),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      success(t("Purchase return downloaded successfully"));
    } catch (error) {
      console.error("Failed to download purchase return:", error);
      // Server message (e.g. 403/404 JSON body) is surfaced as-is
      err(
        error instanceof Error && error.message
          ? error.message
          : t("Failed to download purchase return"),
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleActionDone = (updated?: PurchaseReturn) => {
    const targetId = action?.target.id;
    setAction(null);
    refreshAll();
    if (targetId && detailReturn?.id === targetId) {
      refreshDetail(targetId, updated);
    }
  };

  // Buttons available for a return given its status and the user's permissions.
  // Shared by the table row and the detail modal footer.
  const getActions = (pr: PurchaseReturn) => {
    const actions: {
      key: string;
      label: string;
      icon: ComponentType<{ className?: string }>;
      className: string;
      allowed: boolean;
      deniedText: string;
      onClick: () => void;
    }[] = [];

    if (pr.status === "pending") {
      actions.push(
        {
          key: "approve",
          label: t("Approve"),
          icon: CheckCircle,
          className: "text-emerald-600 hover:bg-emerald-50",
          allowed: canApprove,
          deniedText: t(
            "You don't have permission to approve purchase returns",
          ),
          onClick: () => setAction({ type: "approve", target: pr }),
        },
        {
          key: "reject",
          label: t("Reject"),
          icon: XCircle,
          className: "text-orange-600 hover:bg-orange-50",
          allowed: canApprove,
          deniedText: t(
            "You don't have permission to approve purchase returns",
          ),
          onClick: () => setAction({ type: "reject", target: pr }),
        },
        {
          key: "edit",
          label: t("Edit"),
          icon: Edit2,
          className: "text-blue-600 hover:bg-blue-50",
          allowed: canUpdate,
          deniedText: t("You don't have permission to edit purchase returns"),
          onClick: () => handleEdit(pr),
        },
        {
          key: "delete",
          label: t("Delete"),
          icon: Trash2,
          className: "text-red-600 hover:bg-red-100",
          allowed: canDelete,
          deniedText: t("You don't have permission to delete purchase returns"),
          onClick: () => handleDelete(pr),
        },
      );
    }

    if (pr.status === "approved") {
      actions.push(
        {
          key: "complete",
          label: t("Complete"),
          icon: PackageCheck,
          className: "text-green-600 hover:bg-green-50",
          allowed: canComplete,
          deniedText: t(
            "You don't have permission to complete purchase returns",
          ),
          onClick: () => setAction({ type: "complete", target: pr }),
        },
        {
          key: "cancel",
          label: t("Cancel Return"),
          icon: Ban,
          className: "text-orange-600 hover:bg-orange-50",
          allowed: canCancel,
          deniedText: t("You don't have permission to cancel purchase returns"),
          onClick: () => setAction({ type: "cancel", target: pr }),
        },
      );
    }

    if (pr.status === "completed") {
      actions.push({
        key: "refund",
        label: t("Update Refund Status"),
        icon: Receipt,
        className: "text-purple-600 hover:bg-purple-50",
        allowed: canUpdate,
        deniedText: t("You don't have permission to edit purchase returns"),
        onClick: () => setAction({ type: "refund", target: pr }),
      });
    }

    return actions;
  };

  if (initialLoading) {
    return <LoadingComponent title={t("Purchase Returns")} />;
  }

  const finalTotalItems = apiTotalItems;
  const finalTotalPages = Math.ceil(finalTotalItems / itemsPerPage);
  const hasActiveFilters =
    !!search ||
    statusFilter !== "all" ||
    supplierFilter !== "all" ||
    !!startDate ||
    !!endDate;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setSupplierFilter("all");
    setStartDate("");
    setEndDate("");
  };

  return (
    <div className="min-h-screen bg-[#f2e6db]  p-4 sm:p-6 lg:p-8">
      <div className="mx-auto">
        {/* Header */}
        <div className="py-4">
          <div className="flex flex-col gap-6 bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border border-gray-100 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-5 min-w-0">
              <div className="relative">
                <div className="relative bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                  <PackageMinus className="w-8 h-8 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <div className="min-w-0">
                <h1 className="text-3xl sm:text-4xl font-bold gradient-text mb-1">
                  {t("Purchase Returns")}
                </h1>
                <p className="text-gray-600 text-sm">
                  {t("Return received goods to suppliers")}
                </p>
              </div>
            </div>

            <Tooltip
              title={
                canCreate
                  ? t("New Purchase Return")
                  : t("You don't have permission to create purchase returns")
              }
              placement="top"
            >
              <span>
                <button
                  disabled={!canCreate}
                  onClick={openNewReturnModal}
                  className="flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-700 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-5 h-5" />
                  {t("New Return")}
                </button>
              </span>
            </Tooltip>
          </div>
        </div>

        {/* Stats Section */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
            <StatCard
              icon={PackageMinus}
              title={t("Count")}
              value={stats.total}
              color="blue"
            />
            <StatCard
              icon={TrendingUp}
              title={t("Total")}
              value={toNumber(stats.totalValue)}
              color="green"
              isMoney={true}
            />
            <StatCard
              icon={Clock}
              title={t("Pending")}
              value={stats.pending}
              color="yellow"
            />
            <StatCard
              icon={AlertTriangle}
              title={t("Awaiting Completion")}
              value={stats.approved}
              color="orange"
            />
            <StatCard
              icon={CheckCircle}
              title={t("Completed")}
              value={stats.completed}
              color="purple"
            />
            <StatCard
              icon={Receipt}
              title={t("Pending Refunds")}
              value={stats.pendingRefunds}
              color="red"
            />
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white justify-between rounded-3xl shadow-2xl p-4 sm:p-8 border border-gray-100">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("Search returns...")}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            >
              <option value="all">{t("All Status")}</option>
              {Object.entries(PURCHASE_RETURN_STATUS_CONFIG).map(
                ([value, config]) => (
                  <option key={value} value={value}>
                    {t(config.label)}
                  </option>
                ),
              )}
            </select>
            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            >
              <option value="all">{t("All Suppliers")}</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {getPersonName(supplier, supplier.id)}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
              aria-label={t("Start Date")}
              className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            />
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              aria-label={t("End Date")}
              className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            />
          </div>
          <div className="mt-3 sm:mt-4 flex items-center justify-between">
            <span className="text-xs sm:text-sm text-gray-600">
              {t("Showing")} {purchaseReturns.length} of {finalTotalItems}{" "}
              {t("returns")}
            </span>
            <div className="flex items-center gap-4">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs sm:text-sm text-gray-600 hover:text-black font-medium"
                >
                  {t("Clear Filters")}
                </button>
              )}
              <Tooltip title={t("Refresh Purchase Returns")}>
                <button
                  onClick={refreshAll}
                  className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {t("Refresh")}
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Returns Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-4 relative">
        {loading && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
          </div>
        )}
        <div className="overflow-x-auto">
          {purchaseReturns.length === 0 ? (
            <div className="text-center py-12 px-4 bg-white rounded-3xl">
              <PackageMinus className="mx-auto w-12 h-12 sm:w-16 sm:h-11 text-gray-400" />
              <p className="mt-4 text-sm sm:text-base text-gray-600">
                {t("No purchase returns found")}
              </p>
            </div>
          ) : (
            <table className="w-full table-auto">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Return #")}
                  </th>
                  <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Purchase Order")}
                  </th>
                  <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Supplier")}
                  </th>
                  <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Status")}
                  </th>
                  <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Total")}
                  </th>
                  <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Refund Status")}
                  </th>
                  <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Date")}
                  </th>
                  <th className="px-4 py-4 w-[120px] sm:w-[160px] text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {purchaseReturns.map((pr) => (
                  <tr
                    key={pr.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 font-semibold text-gray-900 break-words">
                      {pr.returnNumber}
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 text-gray-700 break-words">
                      {pr.purchaseOrder?.poNumber || pr.purchaseOrderId}
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 text-gray-700 break-words">
                      {getPersonName(pr.supplier, pr.supplierId)}
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4">
                      <PurchaseReturnStatusBadge status={pr.status} />
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 font-semibold text-gray-900">
                      <Price amount={pr.total} />
                    </td>
                    <td className="hidden md:table-cell px-6 py-4">
                      <RefundStatusBadge status={pr.refundStatus} />
                    </td>
                    <td className="hidden md:table-cell px-2 py-4 text-gray-700">
                      {formatDate(pr.createdAt)}
                    </td>
                    <td className="px-3 py-4 w-[120px] sm:w-[160px] align-top">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {getActions(pr).map((item) => {
                          const Icon = item.icon;
                          return (
                            <Tooltip
                              key={item.key}
                              title={
                                item.allowed ? item.label : item.deniedText
                              }
                              placement="top"
                            >
                              <span>
                                <button
                                  disabled={!item.allowed}
                                  onClick={item.onClick}
                                  className={`p-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${item.className}`}
                                >
                                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                                </button>
                              </span>
                            </Tooltip>
                          );
                        })}
                        <Tooltip title={t("View Purchase Return")}>
                          <button
                            onClick={() => handleView(pr)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        </Tooltip>
                        {pr.status === "completed" && (
                          <Tooltip title={t("Download PDF")}>
                            <button
                              onClick={() => handleDownload(pr)}
                              disabled={downloadingId === pr.id}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {downloadingId === pr.id ? (
                                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                              )}
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {finalTotalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={finalTotalPages}
            totalItems={finalTotalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setItemsPerPage(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            showPageSizeSelector={true}
            isLoading={loading}
            compact={true}
          />
        )}
      </div>

      {/* Create / Edit Modal */}
      {formModal.open && (
        <PurchaseReturnFormModal
          onClose={closeFormModal}
          onSaved={handleFormSaved}
          editingReturn={formModal.editing}
          initialPurchaseOrderId={formModal.purchaseOrderId}
        />
      )}

      {/* Detail Modal */}
      {detailReturn && (
        <PurchaseReturnDetailModal
          purchaseReturn={detailReturn}
          actions={getActions(detailReturn)}
          isDownloading={downloadingId === detailReturn.id}
          onDownload={() => handleDownload(detailReturn)}
          onClose={() => setDetailReturn(null)}
        />
      )}

      {/* Status Action Modal */}
      {action && (
        <PurchaseReturnActionModal
          type={action.type}
          purchaseReturn={action.target}
          onClose={() => setAction(null)}
          onDone={handleActionDone}
        />
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        icon={Trash2}
        iconColor="text-red-600"
        iconBg="bg-red-100"
        onConfirm={confirmConfig.onConfirm}
        onCancel={closeConfirmModal}
      />
    </div>
  );
};

// ==================== DETAIL MODAL ====================
interface DetailAction {
  key: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  allowed: boolean;
  deniedText: string;
  onClick: () => void;
}

interface TimelineEntry {
  status: string;
  timestamp?: string | null;
  actor?: string;
  note?: string | null;
}

const buildTimeline = (
  pr: PurchaseReturn,
  t: (key: string) => string,
): TimelineEntry[] => {
  const entries: TimelineEntry[] = [
    {
      status: "pending",
      timestamp: pr.createdAt,
      actor: getPersonName(pr.creator, ""),
      note: pr.reason,
    },
  ];

  if (pr.status === "rejected") {
    entries.push({
      status: "rejected",
      timestamp: pr.approvedAt || pr.updatedAt,
      actor: getPersonName(pr.approver, ""),
      note: pr.rejectionReason,
    });
    return entries;
  }

  if (pr.approvedAt) {
    entries.push({
      status: "approved",
      timestamp: pr.approvedAt,
      actor: getPersonName(pr.approver, ""),
    });
  }

  if (pr.status === "completed") {
    entries.push({
      status: "completed",
      timestamp: pr.completedAt || pr.updatedAt,
      note: pr.debitNoteNumber
        ? `${t("Debit Note #")}: ${pr.debitNoteNumber}`
        : null,
    });
  }

  if (pr.status === "cancelled") {
    entries.push({
      status: "cancelled",
      timestamp: pr.updatedAt,
      note: pr.cancelReason,
    });
  }

  return entries;
};

const PurchaseReturnDetailModal = ({
  purchaseReturn: pr,
  actions,
  isDownloading,
  onDownload,
  onClose,
}: {
  purchaseReturn: PurchaseReturn;
  actions: DetailAction[];
  isDownloading: boolean;
  onDownload: () => void;
  onClose: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const timeline = buildTimeline(pr, t);

  const infoRows: { label: string; value: ReactNode }[] = [
    {
      label: t("Purchase Order"),
      value: pr.purchaseOrder?.poNumber || pr.purchaseOrderId,
    },
    { label: t("Supplier"), value: getPersonName(pr.supplier, pr.supplierId) },
    { label: t("Created By"), value: getPersonName(pr.creator) },
    { label: t("Created At"), value: formatDate(pr.createdAt, true) },
    { label: t("Approved By"), value: getPersonName(pr.approver) },
    { label: t("Completed At"), value: formatDate(pr.completedAt, true) },
    { label: t("Debit Note #"), value: pr.debitNoteNumber || "--" },
    {
      label: t("Refund Amount"),
      value:
        pr.refundAmount !== null && pr.refundAmount !== undefined ? (
          <Price amount={pr.refundAmount} />
        ) : (
          "--"
        ),
    },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-black px-6 py-5 border-b border-gray-200 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">
                {t("Purchase Return Details")}
              </h2>
              <p className="text-sm text-gray-200 mt-1">{pr.returnNumber}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <PurchaseReturnStatusBadge status={pr.status} />
            <RefundStatusBadge status={pr.refundStatus} />
          </div>

          {/* Header info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {infoRows.map((row) => (
              <div key={row.label} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 font-medium">{row.label}</p>
                <p className="text-sm font-semibold text-gray-900 break-words">
                  {row.value}
                </p>
              </div>
            ))}
          </div>

          {/* Terminal reasons */}
          {pr.status === "rejected" && (
            <div className="border-2 border-red-200 bg-red-50 rounded-xl p-4">
              <p className="text-sm font-bold text-red-800">
                {t("Rejection Reason")}
              </p>
              <p className="text-sm text-red-700 mt-1">
                {pr.rejectionReason || "--"}
              </p>
            </div>
          )}
          {pr.status === "cancelled" && (
            <div className="border-2 border-gray-200 bg-gray-50 rounded-xl p-4">
              <p className="text-sm font-bold text-gray-800">
                {t("Cancellation Reason")}
              </p>
              <p className="text-sm text-gray-700 mt-1">
                {pr.cancelReason || "--"}
              </p>
            </div>
          )}

          {(pr.reason || pr.notes) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  {t("Reason")}
                </p>
                <p className="text-sm text-gray-800">{pr.reason || "--"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  {t("Notes")}
                </p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                  {pr.notes || "--"}
                </p>
              </div>
            </div>
          )}

          {/* Items */}
          <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                      {t("Product")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                      {t("Variation")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                      {t("Quantity")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                      {t("Unit Cost")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                      {t("Line Reason")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                      {t("Total")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(pr.items || []).length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-gray-500 text-sm"
                      >
                        {t("No items")}
                      </td>
                    </tr>
                  ) : (
                    (pr.items || []).map((item) => {
                      const display = getItemDisplay(
                        item.Product,
                        item.ProductVariation,
                        i18n.language,
                      );
                      return (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">
                              {display.name || t("Unnamed product")}
                            </div>
                            {display.sku && (
                              <div className="text-xs text-gray-400">
                                SKU: {display.sku}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {display.variationLabel || t("No variations")}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {item.quantity}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Price amount={item.unitCost} />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {item.reason || "--"}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold">
                            <Price amount={item.total} />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="bg-gray-50 rounded-lg p-6 space-y-2">
            <div className="flex items-center justify-between text-sm text-gray-700">
              <span>{t("Subtotal")}</span>
              <Price amount={pr.subtotal} />
            </div>
            <div className="flex items-center justify-between text-sm text-gray-700">
              <span>{t("Tax")}</span>
              <Price amount={pr.tax} />
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-lg font-bold text-gray-700">
                {t("Return Total")}:
              </span>
              <span className="text-2xl font-bold text-black">
                <Price amount={pr.total} />
              </span>
            </div>
          </div>

          {/* Timeline */}
          <div className="border-2 border-gray-200 rounded-xl p-4 bg-white">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock size={17} />
              {t("History")}
            </h4>
            <div>
              {timeline.map((entry, i) => {
                const config =
                  PURCHASE_RETURN_STATUS_CONFIG[entry.status] ||
                  PURCHASE_RETURN_STATUS_CONFIG.pending;
                const isLast = i === timeline.length - 1;
                return (
                  <div key={`${entry.status}-${i}`} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-3 h-3 mt-1.5 rounded-full flex-shrink-0 ${config.bg}`}
                      />
                      {!isLast && (
                        <div className="w-0.5 bg-gray-200 flex-1 min-h-[24px] my-1" />
                      )}
                    </div>
                    <div className={`flex-1 ${isLast ? "" : "pb-4"}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`text-xs font-bold px-2 py-0.5 rounded ${config.bg} ${config.color}`}
                        >
                          {t(config.label)}
                        </span>
                        {entry.actor && (
                          <span className="text-xs text-gray-500">
                            {entry.actor}
                          </span>
                        )}
                        <span className="text-xs text-gray-400 ml-auto">
                          {formatDate(entry.timestamp, true)}
                        </span>
                      </div>
                      {entry.note && (
                        <p className="mt-1 text-sm text-gray-700">
                          {entry.note}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex flex-wrap items-center gap-3 pt-4 border-t-2 border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-semibold"
            >
              {t("Close")}
            </button>
            <div className="flex flex-wrap gap-3 ml-auto">
              {pr.status === "completed" && (
                <button
                  onClick={onDownload}
                  disabled={isDownloading}
                  className="flex items-center gap-2 px-5 py-3 border-2 border-gray-200 text-gray-800 rounded-lg hover:bg-gray-50 transition-colors font-semibold disabled:opacity-50"
                >
                  {isDownloading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  {t("Download PDF")}
                </button>
              )}
              {actions.map((item) => {
                const Icon = item.icon;
                return (
                  <Tooltip
                    key={item.key}
                    title={item.allowed ? "" : item.deniedText}
                    placement="top"
                  >
                    <span>
                      <button
                        disabled={!item.allowed}
                        onClick={item.onClick}
                        className={`flex items-center gap-2 px-5 py-3 rounded-lg transition-colors font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${
                          item.key === "delete"
                            ? "bg-red-600 text-white hover:bg-red-700"
                            : "bg-black text-white hover:bg-gray-700"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {item.label}
                      </button>
                    </span>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== ACTION MODAL ====================
// One dialog for every status transition so the field handling (required
// reject reason, optional debit note / refund amount) lives in one place.
const PurchaseReturnActionModal = ({
  type,
  purchaseReturn: pr,
  onClose,
  onDone,
}: {
  type: ActionType;
  purchaseReturn: PurchaseReturn;
  onClose: () => void;
  onDone: (updated?: PurchaseReturn) => void;
}) => {
  const { t } = useTranslation();
  const { success, err } = useToast();

  const [text, setText] = useState("");
  const [debitNoteNumber, setDebitNoteNumber] = useState(
    pr.debitNoteNumber || "",
  );
  const [refundAmount, setRefundAmount] = useState(
    pr.refundAmount !== null && pr.refundAmount !== undefined
      ? String(pr.refundAmount)
      : "",
  );
  const [refundStatus, setRefundStatus] = useState<PurchaseReturnRefundStatus>(
    pr.refundStatus === "not_applicable" ? "pending" : pr.refundStatus,
  );
  const [submitting, setSubmitting] = useState(false);
  const [itemCount, setItemCount] = useState<number | undefined>(
    pr.items?.length,
  );

  // List rows may not carry items; the complete warning needs the count
  useEffect(() => {
    if (type !== "complete" || pr.items) return;
    let ignore = false;
    PurchaseReturnsApi.getById(pr.id).then((res) => {
      if (!ignore && res?.success && res.data?.items) {
        setItemCount(res.data.items.length);
      }
    });
    return () => {
      ignore = true;
    };
  }, [type, pr.id, pr.items]);

  const config: Record<
    ActionType,
    {
      title: string;
      message: string;
      confirmText: string;
      icon: ComponentType<{ className?: string }>;
      tone: string;
      textLabel?: string;
      textRequired?: boolean;
    }
  > = {
    approve: {
      title: t("Approve Purchase Return"),
      message: t(
        "Approving reserves these items for return. Stock is not deducted until the return is completed.",
      ),
      confirmText: t("Approve"),
      icon: CheckCircle,
      tone: "bg-emerald-100 text-emerald-700",
      textLabel: t("Notes (optional)"),
    },
    reject: {
      title: t("Reject Purchase Return"),
      message: t("The return will be closed and cannot be reopened."),
      confirmText: t("Reject"),
      icon: XCircle,
      tone: "bg-red-100 text-red-700",
      textLabel: t("Rejection Reason"),
      textRequired: true,
    },
    complete: {
      title: t("Complete Purchase Return"),
      message:
        itemCount !== undefined
          ? t(
              "This will reduce stock for {{count}} item(s) — make sure the goods have physically left the warehouse.",
              { count: itemCount },
            )
          : t(
              "This will reduce stock for the returned items — make sure the goods have physically left the warehouse.",
            ),
      confirmText: t("Complete"),
      icon: PackageCheck,
      tone: "bg-orange-100 text-orange-700",
      textLabel: t("Notes (optional)"),
    },
    cancel: {
      title: t("Cancel Purchase Return"),
      message: t("Are you sure you want to cancel this purchase return?"),
      confirmText: t("Cancel Return"),
      icon: Ban,
      tone: "bg-gray-200 text-gray-700",
      textLabel: t("Reason (optional)"),
    },
    refund: {
      title: t("Update Refund Status"),
      message: t(
        "Record the supplier's credit/debit note once it has been issued.",
      ),
      confirmText: t("Save"),
      icon: Receipt,
      tone: "bg-purple-100 text-purple-700",
    },
  };

  const current = config[type];
  const Icon = current.icon;
  const showRefundFields = type === "complete" || type === "refund";
  const isSubmitDisabled = submitting || (current.textRequired && !text.trim());

  const parseRefundAmount = () => {
    if (!refundAmount.trim()) return undefined;
    const parsed = Number(refundAmount);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : NaN;
  };

  const handleSubmit = async () => {
    const amount = parseRefundAmount();
    if (showRefundFields && Number.isNaN(amount)) {
      err(t("Please enter a valid refund amount"));
      return;
    }

    const trimmed = text.trim();
    const optionalRefundFields = {
      ...(debitNoteNumber.trim()
        ? { debitNoteNumber: debitNoteNumber.trim() }
        : {}),
      ...(amount !== undefined ? { refundAmount: amount } : {}),
    };

    setSubmitting(true);
    try {
      let res;
      switch (type) {
        case "approve":
          res = await PurchaseReturnsApi.approve(pr.id, trimmed || undefined);
          break;
        case "reject":
          res = await PurchaseReturnsApi.reject(pr.id, trimmed);
          break;
        case "complete":
          res = await PurchaseReturnsApi.complete(pr.id, {
            ...optionalRefundFields,
            ...(trimmed ? { notes: trimmed } : {}),
          });
          break;
        case "cancel":
          res = await PurchaseReturnsApi.cancel(pr.id, trimmed || undefined);
          break;
        case "refund":
          res = await PurchaseReturnsApi.updateRefundStatus(pr.id, {
            refundStatus,
            ...optionalRefundFields,
          });
          break;
      }

      if (res?.success) {
        const messages: Record<ActionType, string> = {
          approve: t("Purchase return approved successfully"),
          reject: t("Purchase return rejected"),
          complete: t("Purchase return completed and stock updated"),
          cancel: t("Purchase return cancelled"),
          refund: t("Refund status updated"),
        };
        success(messages[type]);
        onDone(res.data);
      } else {
        // e.g. "Insufficient stock to complete return for <product>"
        err(res?.message || t("Failed to update purchase return"));
      }
    } catch (error) {
      console.error(`Failed to ${type} purchase return:`, error);
      err(t("Failed to update purchase return"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div
        className="bg-white rounded-xl w-full max-w-md shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${current.tone}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                {current.title}
              </h2>
              <p className="text-xs text-gray-500">{pr.returnNumber}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p
            className={`text-sm ${
              type === "complete"
                ? "bg-orange-50 border border-orange-200 text-orange-800 rounded-lg p-3 font-medium"
                : "text-gray-700"
            }`}
          >
            {current.message}
          </p>

          {type === "refund" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("Refund Status")}
              </label>
              <select
                value={refundStatus}
                onChange={(e) =>
                  setRefundStatus(e.target.value as PurchaseReturnRefundStatus)
                }
                className="w-full border-2 px-4 border-gray-300 rounded-lg py-2 focus:outline-none focus:border-black"
              >
                <option value="not_applicable">{t("Not Applicable")}</option>
                <option value="pending">{t("Refund Pending")}</option>
                <option value="received">{t("Refund Received")}</option>
              </select>
            </div>
          )}

          {showRefundFields && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Debit Note #")}{" "}
                  <span className="text-gray-400">({t("Optional")})</span>
                </label>
                <input
                  type="text"
                  value={debitNoteNumber}
                  onChange={(e) => setDebitNoteNumber(e.target.value)}
                  className="w-full border-2 px-4 border-gray-300 rounded-lg py-2 focus:outline-none focus:border-black"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Refund Amount")}{" "}
                  <span className="text-gray-400">({t("Optional")})</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder={String(toNumber(pr.total).toFixed(2))}
                  className="w-full border-2 px-4 border-gray-300 rounded-lg py-2 focus:outline-none focus:border-black"
                />
              </div>
              {type === "complete" && (
                <p className="text-xs text-gray-500">
                  {t(
                    "Supplier paperwork can be added later via Update Refund Status.",
                  )}
                </p>
              )}
            </>
          )}

          {current.textLabel && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {current.textLabel}
                {current.textRequired && (
                  <span className="text-red-500"> *</span>
                )}
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                className="w-full border-2 px-4 border-gray-300 rounded-lg py-2 focus:outline-none focus:border-black resize-none"
              />
            </div>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded-lg font-medium"
          >
            {t("Close")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className={`flex-1 py-2 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              isSubmitDisabled
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : type === "reject"
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-black hover:bg-gray-700 text-white"
            }`}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {current.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component (same as the purchase order page)
interface StatCardProps {
  icon: ComponentType<{ className?: string }>;
  title: string;
  value: number;
  color: "blue" | "green" | "orange" | "red" | "purple" | "yellow";
  isMoney?: boolean;
}

const StatCard = ({
  icon: Icon,
  title,
  value = 0,
  color,
  isMoney = false,
}: StatCardProps) => {
  const colorClasses = {
    blue: "bg-[#0d223f]",
    green: "bg-green-500",
    orange: "bg-orange-500",
    red: "bg-red-500",
    purple: "bg-purple-500",
    yellow: "bg-yellow-500",
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-2 hover:shadow-xl transition-shadow duration-300 w-full">
      <div className="flex items-start gap-2">
        <div
          className={`p-2 sm:p-3 ${colorClasses[color]} rounded-lg flex-shrink-0`}
        >
          <Icon className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
        </div>
        <div className="flex-1 break-words">
          <p className="text-xs sm:text-sm text-gray-700 font-medium">
            {title}
          </p>
          <p className="font-bold text-gray-700 text-sm sm:text-lg md:text-xl break-words leading-tight">
            {isMoney ? <Price amount={value} /> : value}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PurchaseReturnManagementPage;
